<template>
    <task-form-loader v-if="loading" />
    <div v-else>
        <h5>{{ taskFormHeader }}</h5>
        <div class="card headings col-12">
            <div class="flex justify-content-between mb-3">
                <h6>{{ label('Task') }} Details and Description:</h6>
                <div>
                    <Button :label="`Go to ${label('Task')}`" icon="pi pi-align-left" @click="gotoTasks" />
                    <Button label="Cancel" icon="pi pi-arrow-left" @click="goBack" class="z-1 ml-2" severity="warning" />
                </div>
            </div>
            <Accordion :multiple="true" :activeIndex="activeTabs">
                <AccordionTab :header="`${label('Task')} Details`">

                    <div v-if="draftTask">
                        <div class=" justify-content-center mb-2 border-2 border-red-500">
                            <div class="flex align-items-center justify-content-center m-2">
                                <div class="text-xg	">
                                    The <b>{{ label('Task') }}</b> is currently in draft mode.
                                </div>
                            </div>
                        </div>
                        <hr />
                    </div>


                    <div class="formgrid grid">
                        <div class="col-2">Author name:</div>
                        <div class="font-bold col-4">{{ taskAuthorName }}</div>
                        <div class="col-2">Author email:</div>
                        <div class="font-bold col-4">{{ taskAuthorEmail }}</div>
                        <div class="col-2">{{ label('Task') }} created:</div>
                        <div class="font-bold col-4">{{ taskCreationDate }}</div>

                        <div class="col-12"><hr /></div>
                    </div>

                    <!-- From  make sure to update the department from data, it should be based on logged user only-->
                    <div class="formgrid grid">
                        <span class="font-bold col-3">From:</span>
                    </div>
                    <!-- Department -->
                    <div class="formgrid grid">
                        <div class="field col-12 md:col-6">
                            <entity-breadcrumb-tree-select
                                v-model="task.from_department_id"
                                label="Department"
                                :options="fromDepartments"
                                :callBackFunc="departmentStore.fetchDepartmentDetails"
                                :error-message="formErrors.fromDepartmentId"
                                :showEntityOptions="entityOptionsVisiblity"
                                :disabled="editMode"
                            />
                        </div>
                    </div>
                    <!-- To -->
                    <div class="formgrid grid">
                        <span class="font-bold col-3">To:</span>
                    </div>
                    <div class="formgrid grid">
                        <div class="field col-12 md:col-6">
                            <entity-breadcrumb-tree-select
                                v-model="task.to_department_id"
                                label="Department"
                                :options="departmentVueTree"
                                :callBackFunc="departmentStore.fetchDepartmentDetails"
                                :error-message="formErrors.toDepartmentId"
                                :showEntityOptions="entityOptionsVisiblity"
                                :disabled="editMode"
                            />
                            <!-- departmentToChange is needed to load users based on the active department to selected -->
                        </div>
                    </div>
                    <!-- Assign To and Notify By Email -->
                    <div class="formgrid grid">
                        <div class="field col-12 md:col-6 ta-form-assign">
                            <MultiSelect
                                v-model="task.assigned_to"
                                :options="assignToOptions"
                                dataKey="id"
                                optionLabel="fullname"
                                optionValue="id"
                                :filter="true"
                                filterPlaceholder="Search users..."
                                :disabled="editMode"
                                :showClear="true"
                                panelClass="ta-form-assign-panel"
                                class="w-full"
                                :class="{ 'p-invalid': formErrors.assignedTo }"
                            >
                                <template #option="slotProps">
                                    <div :class="slotProps.option.inToDept ? 'ta-form-assign--dept-user' : 'ta-form-assign--other-user'">
                                        {{ slotProps.option.fullname }}
                                    </div>
                                </template>
                                <template #chip="slotProps">
                                    <span :class="getAssignToChipClass(slotProps.value)">
                                        {{ getAssignToChipLabel(slotProps.value) }}
                                    </span>
                                </template>
                            </MultiSelect>
                            <small v-if="formErrors.assignedTo" class="p-error">{{ formErrors.assignedTo }}</small>
                        </div>
                        <div class=" col-12 md:col-6">
                            <div class="field border-dashed border-100 p-2">

                                <UserPickerIgnis label="Notify By Email:" @selectedUsers="selectedUsersNotified" :notified_to="task.notified_to"/>

                                <hr v-if="notifiedUserList.length > 0"/>

                                <Badge
                                    v-for="user in notifiedUserList"
                                    :key="user.id"
                                    :value="user.name + ' ' + user.surname"
                                    removable
                                    class="mr-2 mb-1"
                                />
                            </div>
                        </div>
                    </div>
                    <!-- Source and Theme -->
                    <div class="formgrid grid">
                        <div class="field col-12 md:col-6">
                            <entity-breadcrumb-tree-select
                                v-model="task.source_id"
                                label="Source"
                                :options="sourceVueTree"
                                :callBackFunc="sourceStore.fetchSourceDetails"
                                :error-message="formErrors.sourceId"
                                :showEntityOptions="entityOptionsVisiblity"
                            />
                        </div>
                        <div class="field col-12 md:col-6">
                            <entity-breadcrumb-tree-select v-model="task.theme_id" label="Theme" :options="themeVueTree" :callBackFunc="themeStore.fetchThemeDetails" :error-message="formErrors.themeId" :showEntityOptions="entityOptionsVisiblity" />
                        </div>
                    </div>
                    <!-- Task or Action Title -->
                    <div class="formgrid grid">
                        <div class="field col-12">
                            <base-text-input v-model="task.title" :label="`${label('Task')} or Action Title`" :placeholder="`Enter ${label('Task').toLowerCase()} title`" :bold="true" :error-message="formErrors.title" />
                        </div>
                    </div>

                    <TaskPickerIgnis :selectedTasks="task.linked_tasks" @linkedTaskUpdate="linkedTaskUpdate" :currentTaskId="taskId" />
                    <!-- Associated Tasks -->
                    <!--
                    <div class="formgrid grid">
                        <div class="field col-12">
                            <AssociatedTasksDropdown v-model="task.linked_tasks" :editMode="editMode" />
                        </div>
                    </div>
                    -->
                    <!-- Priority Set and Progress Status -->
                    <div class="formgrid grid">
                        <div class="field col">
                            <base-dropdown v-model="task.priority" :options="priorityItems" optionLabel="name" optionValue="id" label="Priority Set" :bold="true" :error-message="formErrors.priority" />
                        </div>
                        <div class="field col">
                            <base-dropdown v-model="task.progress_status" :options="progressItems" optionLabel="name" optionValue="id" label="Progress Status" :bold="true" :error-message="formErrors.progressStatus" />
                        </div>
                        <div v-if="isFeatureEnabled" class="field col">
                            <base-dropdown v-model="task.assurance_level" :options="assuranceLevelOptions"  optionLabel="percentage"  optionValue="id"  label="PROGRESS TO COMPLETION" :bold="true" :filter="false" :showClear="false"  />
                        </div>
                    </div>
                    <!-- Dates -->
                    <div class="formgrid grid">
                        <div class="field col">
                            <base-calendar-input v-model="task.start_date" label="Start Date" dateFormat="dd-mm-yy" :bold="true" />
                        </div>
                        <div class="field col">
                            <base-calendar-input v-model="task.due_date" label="Due Date" dateFormat="dd-mm-yy" :minDate="minDueDate" :disabled="task.start_date ? false : true" :bold="true" :error-message="formErrors.dueDate" />
                        </div>
                        <div class="field col">
                            <base-calendar-input v-model="task.reminder_date" label="Reminder Date" dateFormat="dd-mm-yy" :bold="true" />
                        </div>
                    </div>
                </AccordionTab>

                <AccordionTab :header="`${label('Task')} or Action Description`">
                    <div class="formgrid grid">
                        <div class="col taskDescription">
                            <BaseEditor v-model="task.description" height="320px" />
                        </div>
                    </div>
                </AccordionTab>

                <AccordionTab header="Images Uploads">
                    <!-- <base-file-upload ref="FileUploading" @has-files="hasFiles" @files-uploaded="saveFileIds" :showLabel="false" /> -->
                    <div class="compact-uploader">
                        <CustomFileUpload ref="FileUploading" @has-files="hasFiles" @files-uploaded="saveFileIds" :showLabel="false" />
                    </div>
                </AccordionTab>

                <AccordionTab v-if="editMode" header="All Existing Attachments">
                    <div v-if="task.attachment?.length > 0">
                        <ul>
                            <li
                                v-for="item in task.attachment"
                                :key="item.id"
                                class="flex items-center justify-between py-2 border-b border-gray-200"
                            >
                                <!-- File name as download link -->
                                <a
                                    href="#"
                                    class="text-blue-600 hover:underline mr-2 mt-2"
                                    @click.prevent="downloadFile(item?.file?.id, item?.file?.original_name)"
                                >
                                    {{ item?.file?.original_name }}
                                </a>

                                <!-- Remove button -->
                                <Button v-show="authStore.hasPermission(UserPermission.WRITE_TASK)"
                                    icon="pi pi-trash"
                                    class="p-button-rounded p-button-danger p-button-sm"
                                    @click.prevent="removeAttachment(item?.file?.id, task)"
                                    v-tooltip="'Delete attachment'"
                                />
                            </li>
                        </ul>
                    </div>
                    <div v-else>No attachments available</div>
                </AccordionTab>
                <AccordionTab v-if="editMode" header="Existing Images Preview">
                    <gallery-image-preview :attachments="task.attachment" />
                </AccordionTab>

                <AccordionTab header="Events">
                    <LogViewer :event_type="logEventTypes" :entity_ids="entityLogIds"/>
                </AccordionTab>
            </Accordion>

            <div class="flex justify-content-between my-2">
                <div>
                    <div v-if="task.assigned_to && task.assigned_to.length > 0">
                        <Button v-if="!editMode || draftTask" label="Save as Draft" class="p-button-warning" @click="submit(true)" />
                    </div>
                </div>
                <div>
                    <ScaleLoader v-if="uploading" class="mx-auto" />
                    <Button :label="buttonLabel" @click="submit" />
                    <Button label="Cancel" icon="pi pi-arrow-left" @click="goBack" class="z-1 ml-2" severity="warning" />
                </div>
            </div>
        </div>

        <AccordionTab v-if="loAttachments" header="Learning Outcome Attachments">
            <B>Learning Outcome Attachments</B>
            <p v-if="loAttachments.length === 0">No attachments found</p>
            <gallery-image-preview v-else :attachments="loAttachments" />
        </AccordionTab>   
    </div>
</template>

<script setup>
//import AssociatedTasksDropdown from '../components/AssociatedTasksDropdown.vue';
import CustomFileUpload from '../../../../../modules/custom-inputs/CustomFileUpload.vue';
import GalleryImagePreview from '../../../../../components/GalleryImagePreview.vue';
import TaskFormLoader from '../modules/TaskFormLoader.vue';
import { userCompanyPreference } from '@/composables/CompanyPreferenceComposable';

const {fetchCompanyPreference} = userCompanyPreference()
const isLOAuthorEnabled = ref(false);



import { computed, onMounted, ref, watch, onUnmounted, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';

import {
    authStore,
    departmentStore,
    sourceStore,
    taskStore,
    themeStore,
    formErrorStore,
    fileStore,
    companyStore,
} from '../../../../../store/global-stores';
import { getAllAssuranceLevels, getAllPriorities, getAllProgress } from '../../../constants';
import { useAssuranceColors } from '@/composables/useAssuranceColors';

// validation schemas
import { TaskSchema, handleErrors } from '../../../../../utilities/schemas';
import { showToast } from '../../../../../utilities/toast-handler';
import { executeLoader } from '../../../utils';
import { formatDisplayDate } from '@/utilities/date-formatter';
import UserPickerIgnis from '@/product/ams/components/UserPickerIgnis.vue';
import { authApi } from '@/utilities/axios';
import LogViewer from '@/product/ams/components/LogViewer.vue';
import TaskPickerIgnis from '@/product/ams/components/TaskPickerIgnis.vue';
import { UserPermission } from '@/modules/permission/enum';
import { useLabels } from '@/composables/LabelComposable';

const { label } = useLabels();
const route = useRoute();
const router = useRouter();

// store states
const { loggedUser, isGodUser } = storeToRefs(authStore);
const { sourceVueTree } = storeToRefs(sourceStore);
const { themeVueTree } = storeToRefs(themeStore);
const { formErrors } = storeToRefs(formErrorStore);
const { uploading } = storeToRefs(fileStore);
const { deptUsers, departmentVueTree, loggedUserDepartmentVueTree } = storeToRefs(departmentStore);
const { task, taskFormHeader, editId, editMode, draftTaskDetails } = storeToRefs(taskStore);

// component states
const priorityItems = ref(null);
const progressItems = ref(null);
const filesExist = ref(null);
const FileUploading = ref(null); //ref for file uploads

const loAttachments = ref([])// for LO attachments


const learningOutcomeId = computed(() => {
  if (route.query?.learningOutcomeId) {
    return route.query.learningOutcomeId
  }

  if (task.value?.learning_outcome_id) {
    return task.value.learning_outcome_id
  }

  return null
})

const fetchLoAttachments = async (id) => {
  try {
    const response = await authApi.get(`/v2/learning-outcomes/${id}/attachments`);

    if (response.statusCode === 200 && response.data) {
      const lo = response.data;

      loAttachments.value = lo.attachments?.map(att => ({ file: att })) || [];
    }
  } catch (e) {
    console.error('Failed to load LO attachments', e);
  }
};

watch(learningOutcomeId, (newVal) => {
  if (newVal) {
    fetchLoAttachments(newVal)
  }
}, { immediate: true })

const { applyToDebriefDescription } = setupLoSubmitterMessage();

const minDueDate = ref(null);
const loading = ref(false);

const logEventTypes = [
    'file',
    'task',
]
const notifiedUserList = ref([])

// Store assigned users data from API (available in edit mode, no extra API call)
const assignedUsersData = ref([]);

// Build Assign To options: TO-dept users + already-assigned non-TO-dept users
const assignToOptions = computed(() => {
    const deptOpts = (deptUsers.value || []).map(u => ({
        ...u,
        inToDept: true
    }));
    const deptIds = new Set(deptOpts.map(u => u.id));
    const extraOpts = assignedUsersData.value
        .filter(u => !deptIds.has(u.id))
        .map(u => ({ ...u, inToDept: false }));
    return [...deptOpts, ...extraOpts];
});

function getAssignToChipClass(userId) {
    const opt = assignToOptions.value.find(o => o.id === userId);
    return opt && opt.inToDept ? 'ta-form-assign--dept-user' : 'ta-form-assign--other-user';
}

function getAssignToChipLabel(userId) {
    const opt = assignToOptions.value.find(o => o.id === userId);
    return (opt && opt.fullname) || `User #${userId}`;
}

// computed properties
const taskId = computed(() => {
    return route.query.taskId ? parseInt(route.query.taskId) : null;
});


const draftTask = computed(() => {
    return route.query.draft ? true : false;
});

// For loading from department VuetreeSelect :- From department loads only according to the logged user id
const fromDepartments = computed(() => {
    return isGodUser.value ? departmentVueTree.value : loggedUserDepartmentVueTree.value;
});

// For hiding EntityBreadCrumbTreeSelect  options
const entityOptionsVisiblity = computed(() => {
    return editMode.value || (route.query.copyMode ? true : false);
});

const debriefAddTask = computed(() => {
    return route.query.debriefId ? true : false;
});

const entityLogIds = computed(() => {
    let arrayList = []
    arrayList.push(taskId.value)

    if (task.value.attachment) {
        task.value.attachment.forEach(mapper => {
            console.log('file', mapper)
            arrayList.push(mapper.file.id)
        })
    }

    return arrayList
})

// Active accordion tabs are set on the basis of mode of form
const activeTabs = computed(() => {
    if (editMode.value) {
        return [0, 1, 2, 3, 4];
    } else {
        return [0, 1, 2];
    }
});

// States of button visibility on the form
const buttonLabel = computed(() => {

    if (uploading.value) {
        return 'Uploading Files..'
    } else if (editMode.value && draftTask.value) {
        return 'Publish Task'
    } else if (editMode.value) {
        return `Update ${label('Task')}`
    } else {
        return `Add ${label('Task')}`
    }

    //return uploading.value ? 'Uploading Files..' : editMode.value && !draftTask.value ? 'Update Task' : 'Add Task';
});

const isFeatureEnabled = companyStore.useFeatureAccess('assurance-level');
const { assuranceLabels } = useAssuranceColors();

const assuranceLevelOptions = computed(() => {
    return getAllAssuranceLevels().map(level => ({
        ...level,
        percentage: assuranceLabels.value[level.id] || level.percentage,
    }));
});

// Debug deptUsers
watch(() => deptUsers.value, (newVal) => {
    console.log('deptUsers changed:', newVal);
}, { deep: true });


// watchers
// Load users on the basis of department selected
watch(
    () => task.value.to_department_id,
    async (deptId) => {
        if (deptId) {
            await departmentStore.getDepartmentUsers(deptId);
        }
    }
);

// load min due date value on the basis of start date selected
watch(
    () => task.value.start_date,
    (startDateVal) => {
        // reset due date if start date is changed into dates later than existing due date value
        if (new Date(startDateVal) >= new Date(task.value.due_date)) task.value.due_date = null;
        const nextDay = new Date(startDateVal);
        nextDay.setDate(nextDay.getDate() + 1);
        minDueDate.value = nextDay;
    }
);

const company_id = computed(() => {
    return authStore.getLoggedUserCompanyId;
});

watchEffect(() => {
    getSelectedUsers(task.value.notified_to)
})

async function getSelectedUsers(ids) {

    if (task.value.notified_to.length > 0) {
        let params = {
            company_id: company_id.value,
            page_size: task.value.notified_to.length,
            ids: ids,
        }

        authApi.get('/v2/admin/users', {
            params: params,
        }).then((response) => {

            if (response.statusCode === 200 && Object.hasOwn(response, 'data')) {
                notifiedUserList.value = response.data.data
            }
        })
    } else {
        notifiedUserList.value = []
    }
}

onMounted(() => {
    if(!route.query.copyMode) {
        taskStore.initTask();
    }

    loadServices();
    loadOptions();

    if (company_id.value) {
        fetchCompanyPreference(company_id.value).then((companyPreferences) => {
            if (typeof companyPreferences !== 'undefined' && Object.hasOwn(companyPreferences, 'learning_outcome_author_name_enabled')) {
                isLOAuthorEnabled.value = companyPreferences.learning_outcome_author_name_enabled === 1
            }
        })
    }
});

onUnmounted(() => {
    task.value.notified_to = []
    taskStore.initTask();
    formErrorStore.clearErrors();
});

function selectedUsersNotified(users) {

    task.value.notified_to = []

    users.forEach((user) => {
        task.value.notified_to.push(user.id);
    })
}

// ================= Initial data loading operations ==================>
const loadServices = async () => {
    loading.value = true;
    executeLoader(true);
    await departmentStore.fetchAllDepartments();
    // If its a normal user then use all departments as a source for from department section
    if (!isGodUser.value) {
        const loggedUserId = loggedUser.value?.id;
        await departmentStore.fetchLoggedUserDepartments(loggedUserId);
    }
    await sourceStore.fetchAllSources();
    await themeStore.fetchAllThemes(true);//true = only visible themes
    // check if there is taskId
    // if yes then its edit mode, so you will have to load details and load them into state
    if (taskId.value) {
        editId.value = taskId.value;
        if (draftTask.value == false) {
            await loadEditDetails(taskId.value);
        } else {
            await taskStore.loadTaskBody(draftTaskDetails.value);
            // draft case
           
            if (!task.value.title) {
                router.push({ name: 'DraftTasks' });
            }
        }
    }
    // otherwise continue as it is
    // For Add Task case from Debrief loading learning outcome Id, source, theme, title and description
    if (debriefAddTask.value) {
        loadDebriefTaskDetail();
    }
    executeLoader(false);
    loading.value = false;
};

const loadDebriefTaskDetail = () => {
    task.value.learning_outcome_id = route.query?.learningOutcomeId;
    task.value.source_id = route.query?.source;
    task.value.theme_id = route.query?.theme;

    task.value.title = route.query.learningOutcomeId
        ? route.query.incidentNo + ' - ' + route.query.outcomeType + ': '
        : null;

    const loDetails = route.query?.details || '';
    applyToDebriefDescription(loDetails);
};

const loadOptions = () => {
    priorityItems.value = getAllPriorities();
    progressItems.value = getAllProgress();
};

const taskAuthorName = ref('')
const taskAuthorEmail = ref('')
const taskCreationDate = ref('')

const loadEditDetails = async (id) => {
    const taskDetails = await taskStore.fetchTaskDetails(id);

    taskAuthorName.value = taskDetails?.author?.name + ' ' + taskDetails?.author?.surname;
    taskAuthorEmail.value = taskDetails?.author?.email;
    taskCreationDate.value = formatDisplayDate(taskDetails?.created_at);

    // Save full user objects before loadTaskBody strips them to IDs (no extra API call)
    assignedUsersData.value = (taskDetails.users_assigned_to || []).map(item => ({
        id: item.user?.id,
        name: item.user?.name || '',
        surname: item.user?.surname || '',
        fullname: (item.user?.name || '').charAt(0).toUpperCase() + (item.user?.name || '').slice(1)
            + ' ' + (item.user?.surname || '').charAt(0).toUpperCase() + (item.user?.surname || '').slice(1)
    }));
    await taskStore.loadTaskBody(taskDetails);
};

function linkedTaskUpdate(updatedLinkedTaskList) {
    //console.log('linkedTaskUpdate', updatedLinkedTaskList)
    task.value.linked_tasks = updatedLinkedTaskList;
}

// ================= Submission operations ==================>

const submit = async (draftFlag = false) => {
    try {
        await TaskSchema.validate({ ...task.value }, { abortEarly: false });

        if (draftFlag == true) {
            // check if title exist in case its draft task
            task.value.draft = 1;
        } else {
            // else validate the task properly

            task.value.draft = 0;
        }
        formErrorStore.clearErrors();
        // submit Task
        submitFunctionHandler();
    } catch (error) {
        if (error.inner && typeof error.inner === 'object') {
            const errors = handleErrors(error);
            formErrorStore.addErrors(errors);
            showToast({}, 'Input data invalid');
        }
    }
};

const submitFunctionHandler = async () => {
    if (filesExist.value) {
        //Upload file first if file exist then only add task
        await FileUploading.value.uploadFiles();
    } else {
        if (await submitTask()) {
            handleRedirection();
        }
    }
};

const submitTask = async () => {
    if (editMode.value) {
        return await taskStore.updateExistingTask();
    } else {
        return await taskStore.createTask();
    }
};

// ================= Redirect operations ==================>

// Based on the results handle where to redirect the page to
const handleRedirection = () => {
    setTimeout(() => {
        debriefAddTask.value ? router.push({ name: 'DebriefCreateUpdate', query: { debriefId: route.query.debriefId } }) : goBack();
    }, 2000);
};

const gotoTasks = () => {
    router.push({ name: 'Tasks' });
};

function goBack() {
    router.go(-1)
}

// ================= File related operations ==================>
// If a file is uploaded then it triggers this function and sets the existence of the file
const hasFiles = (event) => {
    filesExist.value = event.hasFile;
};

// triggered after files are uploaded, to make sure task is submitted
const saveFileIds = async (event) => {
    let attachments = [];
    event.files.forEach((item) => {
        attachments.push(item.id);
    });
    task.value.files_attached = [...task.value.files_attached, ...attachments];
    if (await submitTask()) {
        showToast({ severity: 'success', summary: 'Success' }, 'Files uploads successful');
        handleRedirection();
    }
};

const downloadFile = async (fileId, fileName) => {
    await fileStore.downloadFile(fileId, fileName);
};

const removeAttachment = async (fileId, task) => {
    await fileStore.removeFile(fileId, task);
}

function setupLoSubmitterMessage() {
    const submitter = ref(null);

    const submitterLoId = computed(() => {
        if (route.query?.learningOutcomeId) return route.query.learningOutcomeId;
        if (task.value?.learning_outcome_id) return task.value.learning_outcome_id;
        return null;
    });

    const fetchSubmitter = async (id) => {
        try {
            const response = await authApi.get(`/v2/learning-outcomes/${id}/attachments`);
            if (response.statusCode === 200 && response.data) {
                submitter.value = response.data.author || null;
            }
        } catch (e) {
            console.error('Failed to load LO submitter', e);
        }
    };

    watch(submitterLoId, (id) => {
        if (id) fetchSubmitter(id);
    }, { immediate: true });

    function buildMessage(author) {
        if (!author) return null;

        const fullName = [author.name, author.surname]
            .filter(Boolean)
            .join(' ')
            .trim();

        if (!fullName) return null;

        return `This task has been created from a Learning Outcome originally submitted by ${fullName}.`;
    }

    watch(submitter, (author) => {
        if (!author || editMode.value || route.query.copyMode) return;

        const loMessage = buildMessage(author);
        if (!loMessage) return;

        if (!task.value.description?.includes(loMessage)) {
            task.value.description = `${loMessage}\n\n${task.value.description || ''}`;
        }
    });

    function applyToDebriefDescription(loDetails) {
        const loMessage = buildMessage(submitter.value);
        task.value.description = loMessage ? `${loMessage}\n\n${loDetails}` : loDetails;
    }

    return { applyToDebriefDescription };
}
</script>

<style scoped lang="scss">
.headings {
    padding: 1px auto;
}
.card > h6 {
    margin: 10px auto;
}
.field {
    margin: 0.75rem 0;
    font-size: 1rem;
}
.field > label {
    display: block;
}
.field > span {
    /* display: block; */
    opacity: 0.7;
    margin-left: 4px;
}
.field span.p-tag {
    display: inline-block;
    opacity: 1;
}
.btnContainer {
    display: flex;
    align-content: center;
}

.p-breadcrumb {
    border: none;
}

.compact-uploader :deep(.p-fileupload-header) {
    padding: 0.25rem 0.35rem;
}
.compact-uploader :deep(.p-fileupload-header .p-button) {
    padding: 0.15rem 0.35rem;
    font-size: 0.7rem;
}
.compact-uploader :deep(.p-fileupload-header .p-button .p-button-icon) {
    font-size: 0.7rem;
}
.compact-uploader :deep(.p-fileupload-content) {
    padding: 0.15rem;
    min-height: auto;
}
.compact-uploader :deep(.p-fileupload-content .pi-cloud-upload) {
    font-size: 0.85rem;
    padding: 0.2rem;
    border-width: 1px !important;
}
.compact-uploader :deep(.p-fileupload-content p) {
    margin-top: 0.15rem;
    margin-bottom: 0;
    font-size: 0.65rem;
}
</style>
<style lang="scss" src="@/assets/sass/components/task-ui.scss"></style>

